import React, { useState } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { Spinner, ErrorBanner, Badge, EmptyState } from '../../components/ui';
import { fmt, errMsg } from '../../utils/format';
import api from '../../utils/api';

const STATUS_MAP = {
  pending: 'warning', sent: 'active', paid: 'ok', overdue: 'critical'
};

export default function B2BOrders() {
  const { data, loading, error, refetch } = useFetch('/api/b2b/orders');
  const [selected, setSelected] = useState(null);
  const [sending, setSending] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const deleteOrder = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this B2B order? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await api.delete(`/api/b2b/orders/${id}`);
      if (selected?.id === id) setSelected(null);
      refetch();
    } catch (err) { alert(errMsg(err)); }
    finally { setDeleting(null); }
  };

  const sendInvoice = async (id) => {
    if (!window.confirm('Send invoice now?')) return;
    setSending(id);
    try {
      await api.post(`/api/b2b/orders/${id}/invoice`);
      refetch();
    } catch (err) { alert(errMsg(err)); }
    finally { setSending(null); }
  };

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={error} onRetry={refetch} />;

  return (
    <div>
      <div className="page-header">
        <h1>B2B Orders</h1>
        <div className="page-actions">
          <button className="btn btn-sm" onClick={refetch}><i className="ti ti-refresh" /> Refresh</button>
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>PO Number</th>
                <th>Customer</th>
                <th>Shopify Order</th>
                <th>Invoice #</th>
                <th>Total</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data || []).map(o => (
                <tr key={o.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(selected?.id === o.id ? null : o)}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{o.po_number}</td>
                  <td>{(o.parsed_data?.bill_to?.company) || o.vendor_name || o.raw_email_from || '—'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{o.shopify_order_name || '—'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{o.invoice_number}</td>
                  <td>{o.total_amount ? fmt.currency(o.total_amount) : '—'}</td>
                  <td style={{ fontSize: 12 }}>{o.due_date ? new Date(o.due_date).toLocaleDateString() : '—'}</td>
                  <td><Badge status={STATUS_MAP[o.invoice_status]} label={o.invoice_status} /></td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display:'flex', gap:6 }}>
                    {o.invoice_status === 'pending' && (
                      <button className="btn btn-sm btn-primary" disabled={sending === o.id}
                        onClick={() => sendInvoice(o.id)}>
                        {sending === o.id ? '…' : <><i className="ti ti-send" /> Send invoice</>}
                      </button>
                    )}
                    {o.invoice_status !== 'pending' && (
                      <button className="btn btn-sm" disabled={sending === o.id}
                        onClick={() => sendInvoice(o.id)}>
                        <i className="ti ti-send" /> Resend
                      </button>
                    )}
                    <button className="btn btn-sm btn-danger" disabled={deleting === o.id}
                      onClick={e => deleteOrder(o.id, e)}>
                      {deleting === o.id ? '…' : <i className="ti ti-trash" />}
                    </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!(data || []).length && <EmptyState icon="ti-file-invoice" message="No B2B orders yet — orders arrive via email" />}
        </div>
      </div>

      {selected && (
        <div className="card" style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div className="card-title" style={{ margin: 0 }}>Order detail — {selected.po_number}</div>
            <button className="btn btn-icon" onClick={() => setSelected(null)}><i className="ti ti-x" /></button>
          </div>
          <OrderDetail order={selected} />
        </div>
      )}
    </div>
  );
}

function OrderDetail({ order }) {
  const { data, loading } = useFetch(`/api/b2b/orders/${order.id}`);
  if (loading) return <Spinner />;
  if (!data) return null;
  const parsed = data.parsed_data || {};
  const shipping = parsed.shipping_address || {};

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
      <div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 4 }}>Ship To</div>
        <div style={{ fontSize: 13, lineHeight: 1.6 }}>
          {[shipping.company, `${shipping.first_name||''} ${shipping.last_name||''}`.trim(),
            shipping.address1, shipping.address2,
            `${shipping.city||''}, ${shipping.province_code||''} ${shipping.zip||''}`.trim(),
            shipping.phone].filter(Boolean).map((l,i) => <div key={i}>{l}</div>)}
        </div>
        <div style={{ marginTop:12, fontSize:12, color:'var(--text-tertiary)' }}>From: {data.raw_email_from}</div>
        {data.invoice_sent_at && (
          <div style={{ fontSize:12, color:'var(--text-tertiary)' }}>
            Invoice sent: {new Date(data.invoice_sent_at).toLocaleString()}
          </div>
        )}
      </div>
      <div>
        <div style={{ fontSize:12, color:'var(--text-tertiary)', marginBottom:8 }}>Line Items</div>
        <table style={{ width:'100%', fontSize:13 }}>
          <thead>
            <tr style={{ borderBottom:'1px solid var(--border)' }}>
              <th style={{ textAlign:'left', padding:'4px 8px 4px 0' }}>SKU</th>
              <th style={{ textAlign:'left', padding:'4px 8px' }}>Description</th>
              <th style={{ textAlign:'center', padding:'4px 8px' }}>Qty</th>
              <th style={{ textAlign:'right', padding:'4px 0 4px 8px' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {(data.items||[]).map(item => (
              <tr key={item.id} style={{ borderBottom:'1px solid var(--border)' }}>
                <td style={{ padding:'6px 8px 6px 0', fontFamily:'monospace', fontSize:12 }}>{item.sku}</td>
                <td style={{ padding:'6px 8px' }}>{item.description}</td>
                <td style={{ padding:'6px 8px', textAlign:'center' }}>{item.quantity}</td>
                <td style={{ padding:'6px 0 6px 8px', textAlign:'right', fontWeight:600 }}>{fmt.currency(item.line_total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {parseFloat(data.dropship_fee_pct) > 0 && (() => {
              const subtotal = (data.items||[]).reduce((s,i) => s + parseFloat(i.line_total||0), 0);
              const fee = Math.round(subtotal * (parseFloat(data.dropship_fee_pct)/100) * 100) / 100;
              return (<>
                <tr>
                  <td colSpan={3} style={{ padding:'6px 8px 0', textAlign:'right', color:'var(--text-secondary)', fontSize:12 }}>Merchandise:</td>
                  <td style={{ padding:'6px 0 0 8px', textAlign:'right', fontSize:12 }}>{fmt.currency(subtotal)}</td>
                </tr>
                <tr>
                  <td colSpan={3} style={{ padding:'4px 8px 0', textAlign:'right', color:'var(--text-secondary)', fontSize:12 }}>Drop Ship Fee ({data.dropship_fee_pct}%):</td>
                  <td style={{ padding:'4px 0 0 8px', textAlign:'right', fontSize:12 }}>{fmt.currency(fee)}</td>
                </tr>
                <tr>
                  <td colSpan={3} style={{ padding:'10px 8px 0', textAlign:'right', fontWeight:600 }}>Total:</td>
                  <td style={{ padding:'10px 0 0 8px', textAlign:'right', fontWeight:700, color:'var(--blue)' }}>{fmt.currency(subtotal + fee)}</td>
                </tr>
              </>);
            })()}
            {!parseFloat(data.dropship_fee_pct) && (
              <tr>
                <td colSpan={3} style={{ padding:'10px 8px 0', textAlign:'right', fontWeight:600 }}>Total:</td>
                <td style={{ padding:'10px 0 0 8px', textAlign:'right', fontWeight:700, color:'var(--blue)' }}>
                  {fmt.currency(data.total_amount)}
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>
    </div>
  );
}
